import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

export default function ProfilePage() {
  const { user, profile, isEditor, isTrainingEditor, refreshProfile } = useAuth()
  const [sigUrl, setSigUrl]         = useState(null)
  const [uploading, setUploading]   = useState(false)
  const [removing, setRemoving]     = useState(false)
  const [err, setErr]               = useState('')
  const [success, setSuccess]       = useState('')
  const fileRef = useRef()

  const canUploadSig = isEditor || isTrainingEditor

  // Load current signature preview
  useEffect(() => {
    if (!profile?.signature_path) { setSigUrl(null); return }
    supabase.storage.from('profile-signatures').createSignedUrl(profile.signature_path, 3600)
      .then(({ data }) => setSigUrl(data?.signedUrl || null))
  }, [profile?.signature_path])

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true); setErr(''); setSuccess('')

    // Always overwrite the same path so existing completion records auto-update
    const ext = file.name.split('.').pop().toLowerCase()
    const path = `${user.id}/signature.${ext}`

    // upsert:true replaces the existing file
    const { error: upErr } = await supabase.storage
      .from('profile-signatures')
      .upload(path, file, { upsert: true })

    if (upErr) { setErr(upErr.message); setUploading(false); return }

    const { error: dbErr } = await supabase
      .from('profiles')
      .update({ signature_path: path })
      .eq('id', user.id)

    if (dbErr) { setErr(dbErr.message); setUploading(false); return }

    // Refresh preview and auth context so modals pick up the new path immediately
    const { data } = await supabase.storage.from('profile-signatures').createSignedUrl(path, 3600)
    setSigUrl(data?.signedUrl || null)
    refreshProfile()
    setSuccess('Signature saved.')
    setUploading(false)
    // Reset input so re-selecting same file triggers onChange again
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleRemove = async () => {
    if (!profile?.signature_path) return
    setRemoving(true); setErr(''); setSuccess('')
    await supabase.storage.from('profile-signatures').remove([profile.signature_path])
    await supabase.from('profiles').update({ signature_path: null }).eq('id', user.id)
    setSigUrl(null)
    refreshProfile()
    setSuccess('Signature removed.')
    setRemoving(false)
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-10 space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>

      {/* Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Account</h2>
        <div className="space-y-2 text-sm">
          <div className="flex gap-3">
            <span className="w-28 text-gray-500 shrink-0">Name</span>
            <span className="text-gray-900 font-medium">{profile?.display_name || '—'}</span>
          </div>
          <div className="flex gap-3">
            <span className="w-28 text-gray-500 shrink-0">Email</span>
            <span className="text-gray-900">{user?.email}</span>
          </div>
          <div className="flex gap-3">
            <span className="w-28 text-gray-500 shrink-0">Role</span>
            <span className="text-gray-900 capitalize">{profile?.role?.replace('_', ' ') || '—'}</span>
          </div>
        </div>
      </div>

      {/* Signature */}
      {canUploadSig && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Sign-off Signature</h2>
            <p className="text-xs text-gray-500 mt-1">
              This signature appears on training completion records you sign. Upload a clean image of your handwritten signature (PNG, JPG, or WebP recommended).
            </p>
          </div>

          {/* Preview */}
          <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 min-h-[80px] flex items-center justify-center">
            {sigUrl
              ? <img src={sigUrl} alt="Your signature" className="max-h-16 max-w-[240px] object-contain" />
              : <span className="text-sm text-gray-400">No signature on file</span>
            }
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${uploading ? 'bg-brand-red/60 text-white cursor-not-allowed' : 'bg-brand-red text-white hover:bg-brand-red-dark'}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {uploading ? 'Uploading…' : sigUrl ? 'Replace Signature' : 'Upload Signature'}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                disabled={uploading}
                onChange={handleUpload}
              />
            </label>

            {sigUrl && (
              <button
                onClick={handleRemove}
                disabled={removing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                {removing ? 'Removing…' : 'Remove'}
              </button>
            )}
          </div>

          {err     && <p className="text-xs text-red-600">{err}</p>}
          {success && <p className="text-xs text-green-600">{success}</p>}
        </div>
      )}
    </div>
  )
}
